/**
 * use-reports.ts
 * Los datos de /reportes. Los períodos se leen aquí (son pocos y livianos);
 * Alumnos, Financiero y Exportar llegan del SERVIDOR (`getReportsOverview`,
 * M1 · PR 1, 2b), que lee el seminario completo sin topes y devuelve a cada rol
 * su parte. Antes se leían en el navegador ocho colecciones con topes por
 * colección, y con más datos las cifras salían parciales.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { collection, getDocs, query, orderBy, DocumentData } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ReportsOverview } from '@dosfilos/application';
import { db } from '../lib/firebase';
import { callable } from '../lib/callable';
import { useTenantStore } from '../stores/tenant.store';
import { errorText } from '../lib/error-text';
import { dateLocale, inLocale } from '../lib/i18n-format';
import { pickReportingTerm } from '@dosfilos/shared';

// ─── Output types ─────────────────────────────────────────────────────────────

export interface ReportKpis {
  totalActivePeople: number;
  /** Unique student IDs with at least one 'enrolled' enrollment in the selected term */
  enrolledStudents: number;
  enrollmentRate: number; // 0-100 %
  withActivePlan: number;
  planRate: number; // 0-100 %
  totalCollected: number; // sum of verified payments
  /** Total committed amount across active plans in the selected term */
  totalCommitted: number;
  pendingAmount: number; // sum of installment remainders still to collect
  overdueAmount: number; // subset of pendingAmount where dueDate < today
  currency: string;
}

/** Summary of an academic term, used for the term selector dropdown */
export interface TermSummary {
  id: string;
  name: string;
  status: string; // 'planned' | 'open' | 'closed'
  startDate: string; // ISO date string
}

/** Detail of a single course enrollment for the student drilldown dialog */
export interface EnrolledCourseDetail {
  enrollmentId: string;
  sectionId: string;
  courseId: string;
  courseName: string;
  courseCode: string;
  sectionCode: string;
  programId: string;
  programName: string;
  termName: string;
  termStartDate?: string; // ISO date string, for grouping sort
  sectionStartDate?: string; // ISO date string, for within-group sort
  sectionEndDate?: string; // ISO date string, displayed in the Fechas column
  status: string; // 'enrolled' | 'approved' | 'failed' | 'dropped'
  grade?: number;
  /** La etiqueta del acta («5.5», «B+»), si la hay. */
  gradeLabel?: string;
}

export interface AcademicRow {
  personId: string;
  name: string;
  email: string;
  personType: string;
  enrolledSections: number; // count of 'enrolled' enrollments in selected term
  hasActivePlan: boolean;
  /** True when the student has a tuition exemption applied */
  isExempt: boolean;
  /** Primary program for this student (from their first section enrollment) */
  programId: string;
  programName: string;
  /** Payment plan type label (e.g. '9 cuotas') from active plan, or '—' */
  planType: string;
  /** Enrolled courses detail for the drilldown dialog */
  enrolledCourses: EnrolledCourseDetail[];
}

/** Individual installment detail for the plan drilldown dialog */
export interface InstallmentDetail {
  id: string;
  label: string;
  dueDate: string;
  amount: number;
  type: 'enrollment' | 'monthly';
  isPaid: boolean;
  paidAt: string | null;
  /** Sum of verified payment allocations pointing at this cuota. Greater than
   *  `amount` means it was over-allocated — money imputed where it doesn't fit. */
  allocatedAmount: number;
  /** Computed by the server with the seminary's «today» (`installmentState`):
   *  shown as is, never recomputed in the browser. */
  status: 'paid' | 'overdue' | 'partial' | 'pending';
  /** amount − allocated (never negative). */
  balance: number;
}

export interface FinancialRow {
  planId: string;
  studentId: string;
  studentName: string;
  programName: string;
  planType: string;
  totalAmount: number;
  /** Money actually received: sum of verified `payment.amount`. */
  paidAmount: number;
  /** Money imputed to cuotas: sum of those payments' `allocations`, counting
   *  only allocations to cuotas the plan still has. Should equal `paidAmount` —
   *  when it doesn't, some payment is unimputed or double-imputed and the cuota
   *  checkmarks won't add up to the paid total. */
  allocatedAmount: number;
  /** How many verified payments back `paidAmount`. The domain tolerates a
   *  1-unit rounding gap per payment, so this is the tolerance to compare
   *  `paidAmount` against `allocatedAmount`. */
  verifiedPaymentCount: number;
  pendingAmount: number;
  overdueAmount: number;
  nextDueDate: string | null;
  status: string;
  currency: string;
  installments: InstallmentDetail[];
  /** True when the plan has had a 100% tuition exemption applied (worker-student). */
  isExempt: boolean;
  /** The reason for exemption, when isExempt is true. */
  exemptionReason?: string;
}

/** Per-calendar-month aggregation of planned installments (active plans in selected term) */
export interface MonthlyBreakdownRow {
  /** 'YYYY-MM' */
  monthKey: string;
  /** Human label e.g. 'Enero 2025' */
  label: string;
  /** Number of installments that fall in this month */
  installmentCount: number;
  /** Sum of amounts planned for this month */
  plannedAmount: number;
  /** Sum of amounts from this month's installments that have already been paid */
  paidAmount: number;
}

/** Las pestañas que necesitan la carga pesada (Alumnos, Financiero, Exportar).
 *  El resto solo usa los períodos, y no la espera. */
export const HEAVY_REPORT_TABS: ReadonlySet<number> = new Set([0, 1, 2]);

export interface ReportsData {
  kpis: ReportKpis;
  academicRows: AcademicRow[];
  financialRows: FinancialRow[];
  monthlyBreakdown: MonthlyBreakdownRow[];
  /** All available terms for the term selector dropdown */
  terms: TermSummary[];
  /** Currently selected term ID (null = all terms / unfiltered) */
  selectedTermId: string | null;
  setSelectedTermId: (id: string | null) => void;
  /** La carga inicial (los períodos). */
  loading: boolean;
  /** La carga pesada de Alumnos, Financiero y Exportar, en curso. */
  heavyLoading: boolean;
  /** La carga pesada ya llegó al menos una vez. */
  heavyReady: boolean;
  /** ¿Quien mira recibe la parte Alumnos? (finanzas, no). */
  hasStudentsPart: boolean;
  error: string | null;
  refresh: () => void;
}

/** La pestaña con que abre /reportes: Académico, para todos (M4·D1). Antes,
 *  con `graduation_audit`, abría en Candidatos. */
export const CANDIDATES_TAB = 7;
/** El panel académico (M1 · PR 1): al final, con su valor fijo. */
export const ANALYTICS_TAB = 8;
export function reportsInitialTab(): number {
  return 0;
}

/** El período que se muestra al abrir un informe.
 *
 *  LA REGLA VIVE EN `@dosfilos/shared` porque la usan los dos lados: esta
 *  pantalla y el asistente cuando le preguntan por el trabajo del aula sin
 *  decirle de cuándo. Escrita dos veces, el asistente contestaría sobre un
 *  período mientras la persona mira otro — y los dos números serían correctos,
 *  que es la peor forma de estar mal. */
function pickDefaultTermId(terms: TermSummary[]): string | null {
  return pickReportingTerm(terms)?.id ?? null;
}

const EMPTY_KPIS: ReportKpis = {
  totalActivePeople: 0,
  enrolledStudents: 0,
  enrollmentRate: 0,
  withActivePlan: 0,
  planRate: 0,
  totalCollected: 0,
  totalCommitted: 0,
  pendingAmount: 0,
  overdueAmount: 0,
  currency: 'CLP',
};

/** El mes ('YYYY-MM') escrito en el idioma de la interfaz: «Octubre 2026». */
export function monthLabel(monthKey: string, lang: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  if (!y || !m) return monthKey;
  const text = inLocale(dateLocale(lang))
    .date(new Date(Date.UTC(y, m - 1, 1)), { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .replace(' de ', ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Los códigos del servidor, en el idioma de la interfaz. */
export function toReportsView(
  o: ReportsOverview,
  t: TFunction,
  lang: string,
  selectedTermName: string | null,
): Pick<ReportsData, 'kpis' | 'academicRows' | 'financialRows' | 'monthlyBreakdown'> {
  const planLabel = (opt: string | null) =>
    opt ? t(`sis:reports.planOptions.${opt}`, { defaultValue: opt }) : '—';
  const s = o.students;
  const f = o.financial;
  return {
    kpis: {
      ...EMPTY_KPIS,
      ...(s?.kpis ?? {}),
      ...(f?.kpis ?? {}),
    },
    academicRows: (s?.rows ?? []).map((r) => ({
      personId: r.personId,
      name: r.name,
      email: r.email,
      personType: r.personTypes
        .map((code) => t(`sis:reports.personTypes.${code}`, { defaultValue: code }))
        .join(', '),
      enrolledSections: r.enrolledSections,
      hasActivePlan: r.hasActivePlan,
      isExempt: r.isExempt,
      programId: r.programId,
      programName: r.programName,
      planType: planLabel(r.planOption),
      enrolledCourses: r.enrolledCourses.map((c) => ({
        ...c,
        termName: c.termName ?? selectedTermName ?? t('sis:reports.allTerms'),
      })),
    })),
    financialRows: (f?.rows ?? []).map(({ planOption, ...r }) => ({
      ...r,
      planType: planLabel(planOption),
    })),
    monthlyBreakdown: (f?.monthlyBreakdown ?? []).map((m) => ({
      ...m,
      label: monthLabel(m.monthKey, lang),
    })),
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

const getReportsOverview = callable<{ tenantId: string; termId: string | null }, ReportsOverview>(
  'getReportsOverview',
);

export function useReports(opts: { heavyEnabled?: boolean } = {}): ReportsData {
  const heavyEnabled = opts.heavyEnabled ?? true;
  const { currentTenantId } = useTenantStore();
  const { t, i18n } = useTranslation('sis');

  const [loading, setLoading] = useState(true);
  const [heavyLoading, setHeavyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [terms, setTerms] = useState<TermSummary[]>([]);
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);
  /** La última respuesta del servidor, con el período que pidió. */
  const [overview, setOverview] = useState<ReportsOverview | null>(null);

  const applyTerms = useCallback((docs: DocumentData[]) => {
    const termSummaries: TermSummary[] = docs.map((term) => ({
      id: term.id as string,
      name: (term.name as string | undefined) ?? term.id,
      status: (term.status as string | undefined) ?? 'planned',
      startDate: (term.startDate as string | undefined) ?? '',
    }));
    setTerms(termSummaries);
    setSelectedTermId((prev) => (prev !== null ? prev : pickDefaultTermId(termSummaries)));
  }, []);

  // ── Los períodos: siempre, sin tope y livianos ─────────────────────────────
  const loadTerms = useCallback(async () => {
    if (!currentTenantId) return;
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'tenants', currentTenantId, 'terms'), orderBy('startDate', 'desc')),
      );
      applyTerms(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as DocumentData));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [currentTenantId, applyTerms]);

  // ── Alumnos y Financiero, del servidor, para el período elegido ────────────
  const load = useCallback(async () => {
    if (!currentTenantId || loading) return;
    setHeavyLoading(true);
    setError(null);
    try {
      const res = await getReportsOverview({ tenantId: currentTenantId, termId: selectedTermId });
      setOverview(res.data);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setHeavyLoading(false);
    }
  }, [currentTenantId, selectedTermId, loading]);

  // Al cambiar de seminario: períodos de nuevo y la carga pesada descartada.
  useEffect(() => {
    setOverview(null);
    void loadTerms();
  }, [loadTerms]);

  // La carga pesada cuando una pestaña la necesita, y otra vez al cambiar de período.
  const stale = !overview || overview.termId !== selectedTermId;
  useEffect(() => {
    if (heavyEnabled && stale && !heavyLoading) void load();
  }, [heavyEnabled, stale, load]);

  const refresh = useCallback(async () => {
    await loadTerms();
    if (heavyEnabled || overview) await load();
  }, [loadTerms, load, heavyEnabled, overview]);

  const view = useMemo(() => {
    if (!overview) {
      return { kpis: EMPTY_KPIS, academicRows: [], financialRows: [], monthlyBreakdown: [] };
    }
    const termName = overview.termId
      ? (terms.find((x) => x.id === overview.termId)?.name ?? overview.termId)
      : null;
    return toReportsView(overview, t, i18n.language, termName);
  }, [overview, t, i18n.language, terms]);

  return {
    ...view,
    terms,
    selectedTermId,
    setSelectedTermId,
    loading,
    heavyLoading,
    heavyReady: overview !== null && !stale,
    hasStudentsPart: overview?.students != null,
    error,
    refresh,
  };
}
